import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  HORMONE_REFERENCE_PHASE_LABEL_KEYS,
  HORMONE_REFERENCE_PHASE_ORDER,
  HORMONE_REFERENCE_RANGES,
  HORMONE_REFERENCE_SERIES_ORDER,
} from '../../../common/hormone-reference-ranges';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

const formatReferenceValue = (value: number): string => value.toFixed(1);

const OvHormoneReferenceRanges: FC = () => {
  const { t } = useTranslation();

  return (
    <Container data-testid="hormone-reference-ranges">
      <Title>{t('hormoneTrends.referenceRanges.title')}</Title>
      <Cards>
        {HORMONE_REFERENCE_SERIES_ORDER.map((seriesId) => {
          const series = HORMONE_REFERENCE_RANGES[seriesId];

          return (
            <Card
              key={series.id}
              data-testid={`hormone-reference-range-${series.id}`}
            >
              <CardHeader>
                <SeriesName>{series.displayName}</SeriesName>
                {series.expansionKey && (
                  <Expansion>{t(series.expansionKey)}</Expansion>
                )}
                <Unit>{series.unit}</Unit>
              </CardHeader>
              <PhaseGrid>
                {HORMONE_REFERENCE_PHASE_ORDER.map((phaseId) => {
                  const range = series.ranges[phaseId];

                  return (
                    <PhaseColumn key={phaseId} data-reference-phase={phaseId}>
                      <PhaseLabel>
                        {t(HORMONE_REFERENCE_PHASE_LABEL_KEYS[phaseId])}
                      </PhaseLabel>
                      <RangeValue>
                        {formatReferenceValue(range.low)}–
                        {formatReferenceValue(range.high)}
                      </RangeValue>
                    </PhaseColumn>
                  );
                })}
              </PhaseGrid>
            </Card>
          );
        })}
      </Cards>
      <Disclaimer>{t('hormoneTrends.referenceRanges.disclaimer')}</Disclaimer>
    </Container>
  );
};

export default OvHormoneReferenceRanges;

const Container = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-top: 1rem;
`;

const Title = styled.h3`
  margin: 0;
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.LARGE};
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: 56rem) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.article`
  overflow: hidden;
  border: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  border-radius: ${Variables.borderRadius.LARGE};
  background-color: ${Colours.OV_WHITE};
`;

const CardHeader = styled.header`
  display: flex;
  align-items: baseline;
  gap: 0.375rem;
  padding: 0.75rem;
  background-color: ${Colours.OV_BASE};
  color: ${Colours.WHITE};
`;

const SeriesName = styled.span`
  font-size: ${Variables.fontSizes.LARGE};
  font-weight: bold;
`;

const Expansion = styled.span`
  min-width: 0;
  font-size: ${Variables.fontSizes.SMALL};
`;

const Unit = styled.span`
  margin-left: auto;
  font-size: ${Variables.fontSizes.SMALL};
  white-space: nowrap;
`;

const PhaseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
`;

const PhaseColumn = styled.div`
  min-width: 0;
  padding: 0.75rem 0.5rem;
  border-right: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  text-align: center;

  &:last-child {
    border-right: 0;
  }
`;

const PhaseLabel = styled.div`
  min-height: 2.5rem;
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
  line-height: ${Variables.lineHeights.LARGE};
`;

const RangeValue = styled.div`
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.MEDIUM};
  font-weight: bold;
  white-space: nowrap;
`;

const Disclaimer = styled.p`
  margin: 0;
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
  line-height: ${Variables.lineHeights.LARGE};
`;
